import { Navigate, Outlet, type RouteObject, useLocation } from 'react-router';
import { Layout } from '../components/Layout';
import { Spinner } from '../components/ui';
import { t } from '../i18n';
import { useAuth, useMe } from '../lib/auth';
import { can } from '../lib/permissions';
import { RolesPage } from '../pages/access/RolesPage';
import { UserRolesPage } from '../pages/access/UserRolesPage';
import { DevicesPage, EnrollmentPage } from '../pages/inventory/InventoryPage';
import { KnowledgePage } from '../pages/knowledge/KnowledgePage';
import { ServerPage, TargetPage } from '../pages/monitoring/DetailPages';
import { IncidentsPage, MaintenancePage, MonitoringPage } from '../pages/monitoring/MonitoringPage';
import { LicenseDetailPage } from '../pages/licenses/LicenseDetailPage';
import { LicensesPage } from '../pages/licenses/LicensesPage';
import { ParametersPage } from '../pages/parameters/ParametersPage';
import { isHr } from '../pages/people/data';
import { DirectoryPage } from '../pages/people/DirectoryPage';
import { HireDetailPage } from '../pages/people/HireDetailPage';
import { HrPortalPage } from '../pages/people/HrPortalPage';
import { ImportUsersPage } from '../pages/people/ImportUsersPage';
import { NewHirePage } from '../pages/people/NewHirePage';
import { NewSeparationPage } from '../pages/people/NewSeparationPage';
import { PeopleRequestsPage } from '../pages/people/PeopleRequestsPage';
import { PersonPage } from '../pages/people/PersonPage';
import { SeparationDetailPage } from '../pages/people/SeparationDetailPage';
import { TeamPage } from '../pages/people/TeamPage';
import { OrganizationsPage } from '../pages/platform/OrganizationsPage';
import { TenantsPage } from '../pages/platform/TenantsPage';
import { MyTasksPage } from '../pages/tasks/MyTasksPage';
import { NewTaskPage } from '../pages/tasks/NewTaskPage';
import { SummaryPage } from '../pages/tasks/SummaryPage';
import { SupplierDetailPage } from '../pages/suppliers/SupplierDetailPage';
import { SuppliersPage } from '../pages/suppliers/SuppliersPage';
import { TaskDetailPage } from '../pages/tasks/TaskDetailPage';
import { CanchaPage } from '../pages/projects/CanchaPage';
import { NewProjectPage } from '../pages/projects/NewProjectPage';
import { NewRequestPage } from '../pages/projects/NewRequestPage';
import { ProjectDetailPage } from '../pages/projects/ProjectDetailPage';
import { ProjectsPage } from '../pages/projects/ProjectsPage';
import { RequestDetailPage } from '../pages/projects/RequestDetailPage';
import { RequestsPage } from '../pages/projects/RequestsPage';
import { NewTicketPage } from '../pages/tickets/NewTicketPage';
import { TicketDetailPage } from '../pages/tickets/TicketDetailPage';
import { TicketsPage } from '../pages/tickets/TicketsPage';
import { HomePage } from '../pages/HomePage';
import { LoginPage } from '../pages/LoginPage';
import { MfaPage } from '../pages/MfaPage';
import { ForgotPasswordPage, ResetPasswordPage } from '../pages/PasswordPages';
import { SecurityPage } from '../pages/SecurityPage';

/** Rutas privadas: sin sesión, al login (y de vuelta a donde iba después de entrar). */
function RequireAuth() {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === 'cargando') {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner label={t('comun.cargando')} />
      </div>
    );
  }
  if (state.status === 'anonimo') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

/** Secciones que exigen un permiso (con varios, alguno). Es solo para la interfaz: la API valida cada acción por su cuenta. */
function RequirePermission({ permission, operator = false }: { permission: string | string[]; /** Solo en el tenant operador. */ operator?: boolean }) {
  const me = useMe();
  const allowed = (Array.isArray(permission) ? permission : [permission]).some((p) => can(me, p));
  if (!allowed || (operator && !me.platformOperator)) return <p className="text-sm text-slate-600">{t('acceso.sin_permiso')}</p>;
  return <Outlet />;
}

/** Usuarios: RR. HH. e IT entran al portal de autoservicio; el resto, a sus altas. */
function PeopleHome() {
  return <Navigate to={isHr(useMe()) ? '/usuarios/portal' : '/usuarios/altas'} replace />;
}

const articleEditor = async () => ({ Component: (await import('../pages/knowledge/ArticleEditorPage')).ArticleEditorPage });

function NotFound() {
  return <p className="text-sm text-slate-600">{t('error.no_encontrado')}</p>;
}

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/login/verificacion', element: <MfaPage /> },
  { path: '/recuperar-clave', element: <ForgotPasswordPage /> },
  { path: '/restablecer-clave', element: <ResetPasswordPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Layout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'perfil', element: <Navigate to="/perfil/seguridad" replace /> },
          { path: 'perfil/seguridad', element: <SecurityPage /> },
          // Cualquier usuario abre tickets y ve los suyos; la API filtra el resto según sus permisos.
          { path: 'tickets', element: <TicketsPage /> },
          { path: 'tickets/nuevo', element: <NewTicketPage /> },
          { path: 'tickets/:number', element: <TicketDetailPage /> },
          { path: 'proyectos', element: <ProjectsPage /> },
          { path: 'proyectos/nuevo', element: <NewProjectPage /> },
          { path: 'proyectos/cancha', element: <CanchaPage /> },
          { path: 'proyectos/solicitudes', element: <RequestsPage /> },
          { path: 'proyectos/solicitudes/nueva', element: <NewRequestPage /> },
          { path: 'proyectos/solicitudes/:number', element: <RequestDetailPage /> },
          { path: 'proyectos/:number', element: <ProjectDetailPage /> },
          { path: 'tareas', element: <MyTasksPage /> },
          { path: 'tareas/nueva', element: <NewTaskPage /> },
          { path: 'tareas/resumen', element: <SummaryPage /> },
          { path: 'tareas/:id', element: <TaskDetailPage /> },
          // Altas y bajas las pide cualquiera (un líder); el portal y el directorio son de RR. HH. e IT.
          { path: 'usuarios', element: <PeopleHome /> },
          { path: 'usuarios/altas', element: <PeopleRequestsPage key="altas" kind="altas" /> },
          { path: 'usuarios/altas/nueva', element: <NewHirePage /> },
          { path: 'usuarios/altas/:number', element: <HireDetailPage /> },
          { path: 'usuarios/bajas', element: <PeopleRequestsPage key="bajas" kind="bajas" /> },
          { path: 'usuarios/bajas/nueva', element: <NewSeparationPage /> },
          { path: 'usuarios/bajas/:number', element: <SeparationDetailPage /> },
          // La ficha la ven RR. HH., IT y el jefe directo (lo decide la API); Mi equipo, cualquier líder.
          { path: 'usuarios/equipo', element: <TeamPage /> },
          { path: 'usuarios/personas/:id', element: <PersonPage /> },
          {
            element: <RequirePermission permission={['usuarios.aprobar', 'usuarios.gestionar']} />,
            children: [{ path: 'usuarios/portal', element: <HrPortalPage /> }],
          },
          {
            element: <RequirePermission permission={['usuarios.ver', 'usuarios.aprobar', 'usuarios.gestionar']} />,
            children: [{ path: 'usuarios/directorio', element: <DirectoryPage /> }],
          },
          {
            element: <RequirePermission permission="usuarios.gestionar" />,
            children: [{ path: 'usuarios/importar', element: <ImportUsersPage /> }],
          },
          {
            element: <RequirePermission permission={['inventario.ver', 'inventario.agentes']} />,
            children: [{ path: 'inventario', element: <DevicesPage /> }],
          },
          {
            element: <RequirePermission permission="inventario.agentes" />,
            children: [{ path: 'inventario/enrolamiento', element: <EnrollmentPage /> }],
          },
          {
            element: <RequirePermission permission={['monitoreo.ver', 'monitoreo.gestionar', 'monitoreo.avisos']} />,
            children: [
              { path: 'monitoreo', element: <MonitoringPage /> },
              { path: 'monitoreo/servidor', element: <ServerPage /> },
              { path: 'monitoreo/incidentes', element: <IncidentsPage /> },
              { path: 'monitoreo/mantenimientos', element: <MaintenancePage /> },
              { path: 'monitoreo/sitios/:id', element: <TargetPage /> },
            ],
          },
          // Todos leen la base (la API muestra a cada uno lo que le corresponde); solo IT escribe y ve el historial.
          // Las páginas que dibujan Markdown se cargan al abrirlas: marked y DOMPurify no van en el paquete inicial.
          { path: 'conocimiento', element: <KnowledgePage /> },
          {
            element: <RequirePermission permission="conocimiento.publicar" />,
            children: [
              { path: 'conocimiento/nuevo', lazy: articleEditor },
              { path: 'conocimiento/:code/editar', lazy: articleEditor },
            ],
          },
          {
            element: <RequirePermission permission={['conocimiento.ver_internos', 'conocimiento.publicar']} />,
            children: [{ path: 'conocimiento/:code/versiones/:version', lazy: async () => ({ Component: (await import('../pages/knowledge/ArticlePage')).ArticleVersionPage }) }],
          },
          { path: 'conocimiento/:code', lazy: async () => ({ Component: (await import('../pages/knowledge/ArticlePage')).ArticlePage }) },
          // El administrador de una plataforma ve las suyas (la API filtra); llega por el aviso de renovación.
          { path: 'licencias', element: <LicensesPage /> },
          { path: 'licencias/:id', element: <LicenseDetailPage /> },
          {
            element: <RequirePermission permission={['proveedores.ver', 'proveedores.gestionar']} />,
            children: [
              { path: 'proveedores', element: <SuppliersPage /> },
              { path: 'proveedores/:id', element: <SupplierDetailPage /> },
            ],
          },
          {
            path: 'acceso',
            element: <RequirePermission permission="plataforma.roles.ver" />,
            children: [
              { index: true, element: <Navigate to="/acceso/roles" replace /> },
              { path: 'roles', element: <RolesPage /> },
              { path: 'usuarios', element: <UserRolesPage /> },
            ],
          },
          // El panel decide qué secciones mostrar según los permisos (cada catálogo tiene el suyo).
          { path: 'parametros', element: <ParametersPage section="inicio" /> },
          { path: 'parametros/generales', element: <ParametersPage section="generales" /> },
          { path: 'parametros/calendario', element: <ParametersPage section="calendario" /> },
          { path: 'parametros/feriados', element: <ParametersPage section="feriados" /> },
          { path: 'parametros/catalogos/:catalog', element: <ParametersPage section="catalogo" /> },
          {
            path: 'plataforma',
            element: <RequirePermission permission="plataforma.tenants.gestionar" />,
            children: [
              { index: true, element: <Navigate to="/plataforma/organizaciones" replace /> },
              { path: 'organizaciones', element: <OrganizationsPage /> },
              { path: 'tenants', element: <RequirePermission permission="plataforma.tenants.gestionar" operator />, children: [{ index: true, element: <TenantsPage /> }] },
            ],
          },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
];
